import React, { useState, useEffect, useMemo } from 'react'
import {
    Box, Typography, Avatar, IconButton, Button, Collapse, LinearProgress,
    Table, TableHead, TableBody, TableRow, TableCell, Divider,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import supabase from '../../../../supabaseClient';
import { applyRoundingRules, findMatchingRule } from '../../../../Utils/roundingRules';
import PaymentsModal from './PaymentsModal';
import PaymentHistory from '../PaymentHistory';

const HOURS_COLOR = '#A84355';
const REMAINING_COLOR = '#B5541F';

const MONTH_NAMES = [
    'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
    'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień',
];

const pad = (n) => String(n).padStart(2, '0');
const monthKeyOf = (d) => `${d.getFullYear()}-${d.getMonth()}`;

const fmtPln = (v) =>
    `${v.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł`;

const fmtHM = (totalMinutes) =>
    `${Math.floor(totalMinutes / 60)}h ${pad(Math.round(totalMinutes % 60))}m`;

const fmtDate = (iso) => new Date(iso).toLocaleDateString('pl-PL');
const fmtTime = (iso) => iso
    ? new Date(iso).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })
    : '—';
const fmtDayMonth = (iso) => new Date(iso).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' });
const fmtWeekRange = (weekStart, weekEnd) => `${fmtDayMonth(weekStart)} – ${fmtDayMonth(weekEnd)}`;

const billableMinutesOf = (record, rules) => {
    const matched = findMatchingRule(record.check_in, rules);
    if (matched) return applyRoundingRules(record.check_in, record.check_out, matched).billableMinutes;
    return Math.max(0, (new Date(record.check_out) - new Date(record.check_in)) / 60000);
};

const getInitials = (name, surname) => `${name?.[0] ?? '?'}${surname?.[0] ?? ''}`.toUpperCase();

function MetricBlock({ label, value, valueColor, lines }) {
    return (
        <Box sx={{ minWidth: 110 }}>
            <Typography
                sx={{
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    letterSpacing: '0.5px',
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                }}
            >
                {label}
            </Typography>
            <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: valueColor || 'text.primary' }}>
                {value}
            </Typography>
            {(lines || []).map((line, i) => (
                <Typography key={i} sx={{ fontSize: '0.72rem', color: 'text.secondary', lineHeight: 1.4 }}>
                    {line}
                </Typography>
            ))}
        </Box>
    );
}

const EMPTY_PRODUCTION_HISTORY = { rows: [], totalValue: 0 };

function PaymentCardComponent({ employee, idConfig, dateFrom, dateTo, rules = [], onChanged, productionHistory = EMPTY_PRODUCTION_HISTORY }) {
    const [open, setOpen] = useState(false);
    const [allAttendance, setAllAttendance] = useState([]);
    const [allPayments, setAllPayments] = useState([]);
    const [allAbsences, setAllAbsences] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalPeriodKey, setModalPeriodKey] = useState(null);

    const fetchLifetime = async () => {
        setLoading(true);
        const [{ data: attendanceData }, { data: paymentsData }, { data: absencesData, error: absencesError }] = await Promise.all([
            supabase
                .from('attendance')
                .select('check_in, check_out')
                .eq('employee_id', employee.id)
                .not('check_in', 'is', null)
                .not('check_out', 'is', null),
            supabase
                .from('payments')
                .select('*')
                .eq('employee_id', employee.id),
            supabase
                .from('absences')
                .select('absence_start, hours, hourly_rate')
                .eq('employee_id', employee.id),
        ]);
        setAllAttendance(attendanceData || []);
        setAllPayments(paymentsData || []);
        setAllAbsences(absencesError ? [] : (absencesData || []));
        setLoading(false);
    };

    useEffect(() => { fetchLifetime(); }, [employee.id, rules]);

    const handlePaymentChanged = () => {
        fetchLifetime();
        onChanged?.();
    };

    // Per calendar month: how much was earned (rounding-rule-adjusted hours + leave) vs. paid,
    // split by component. "overtime_bonus" payments have no accrual ledger — they're excluded
    // from earned/remaining and only show up in the cash-paid totals.
    const monthlyBuckets = useMemo(() => {
        const buckets = {};
        const ensure = (key) => (buckets[key] = buckets[key] || {
            earnedHours: 0, earnedLeave: 0, paidHours: 0, paidLeave: 0, paidOther: 0,
        });

        allAttendance.forEach((r) => {
            const key = monthKeyOf(new Date(r.check_in));
            ensure(key).earnedHours += (billableMinutesOf(r, rules) / 60) * (employee.hourly_rate || 0);
        });
        allAbsences.forEach((a) => {
            if (!a.absence_start) return;
            const key = monthKeyOf(new Date(a.absence_start));
            ensure(key).earnedLeave += (a.hours || 0) * (a.hourly_rate || 0);
        });
        allPayments.forEach((p) => {
            const key = monthKeyOf(new Date(p.period_month || p.date));
            const bucket = ensure(key);
            if (p.type === 'leave') bucket.paidLeave += p.amount || 0;
            else if (p.type === 'overtime_bonus') bucket.paidOther += p.amount || 0;
            else bucket.paidHours += p.amount || 0;
        });

        return buckets;
    }, [allAttendance, allAbsences, allPayments, rules, employee.hourly_rate]);

    const selectedKey = monthKeyOf(new Date(dateFrom));

    // Payments settling this period — bucketed by period_month (with date fallback for
    // legacy rows), not by the day the transaction was made. Consistent with monthlyBuckets.
    const monthPayments = useMemo(
        () => allPayments
            .filter((p) => monthKeyOf(new Date(p.period_month || p.date)) === selectedKey)
            .sort((a, b) => new Date(b.date) - new Date(a.date)),
        [allPayments, selectedKey]
    );

    const monthAttendanceList = useMemo(
        () => allAttendance.filter((r) => monthKeyOf(new Date(r.check_in)) === selectedKey),
        [allAttendance, selectedKey]
    );

    const monthHoursMinutes = useMemo(
        () => monthAttendanceList.reduce((sum, r) => sum + billableMinutesOf(r, rules), 0),
        [monthAttendanceList, rules]
    );

    const monthSalary = (monthHoursMinutes / 60) * (employee.hourly_rate || 0);
    const monthPaidAmount = monthlyBuckets[selectedKey]?.paidHours || 0;

    const monthAbsences = useMemo(
        () => allAbsences.filter((a) => a.absence_start && monthKeyOf(new Date(a.absence_start)) === selectedKey),
        [allAbsences, selectedKey]
    );
    const monthLeaveHours = monthAbsences.reduce((sum, a) => sum + (a.hours || 0), 0);
    const monthLeaveAmount = monthAbsences.reduce((sum, a) => sum + (a.hours || 0) * (a.hourly_rate || 0), 0);
    const monthLeavePaid = monthlyBuckets[selectedKey]?.paidLeave || 0;

    // Dodatek produkcyjny liczony tylko dla aktualnie oglądanego miesiąca (bez pobierania
    // historii "od zawsze") — ile wyliczono z produkcji minus ile już zapłacono za ten okres
    // (paidOther z monthlyBuckets, czyli po period_month, a nie po dacie transakcji).
    const productionBonusPaidThisMonth = monthlyBuckets[selectedKey]?.paidOther || 0;
    const productionBonusRemaining = Math.max(0, productionHistory.totalValue - productionBonusPaidThisMonth);

    const currentYear = new Date(dateFrom).getFullYear();
    const monthLabel = (key) => {
        const [y, m] = key.split('-').map(Number);
        return y === currentYear ? MONTH_NAMES[m] : `${MONTH_NAMES[m]} ${String(y).slice(-2)}`;
    };

    // Rejestr "nierozliczonych okresów" (Godziny pracy + Urlop) rozszerzony o dodatek
    // produkcyjny — ale wyłącznie dla aktualnie oglądanego miesiąca (selectedKey), bo tylko
    // dla niego mamy policzoną "Historię produkcji". Starsze miesiące nie dostają otherRemaining
    // (celowe uproszczenie: unikamy pobierania produkcji za cały czas istnienia pracownika).
    const unpaidMonths = useMemo(() => {
        const keys = new Set(Object.keys(monthlyBuckets));
        keys.add(selectedKey);
        return Array.from(keys)
            .map((key) => {
                const b = monthlyBuckets[key] || { earnedHours: 0, earnedLeave: 0, paidHours: 0, paidLeave: 0 };
                const hoursRemaining = Math.max(0, b.earnedHours - b.paidHours);
                const leaveRemaining = Math.max(0, b.earnedLeave - b.paidLeave);
                const otherRemaining = key === selectedKey ? productionBonusRemaining : 0;
                const otherEarned = key === selectedKey ? productionHistory.totalValue : 0;
                const otherPaid = key === selectedKey ? productionBonusPaidThisMonth : 0;
                const [y, m] = key.split('-').map(Number);
                return {
                    key,
                    label: monthLabel(key),
                    monthDate: `${y}-${pad(m + 1)}`,
                    hoursRemaining,
                    leaveRemaining,
                    otherRemaining,
                    remaining: hoursRemaining + leaveRemaining + otherRemaining,
                    earned: b.earnedHours + b.earnedLeave + otherEarned,
                    paid: b.paidHours + b.paidLeave + otherPaid,
                };
            })
            .filter((m) => m.remaining > 0.005)
            .sort((a, b) => {
                const [ay, am] = a.key.split('-').map(Number);
                const [by, bm] = b.key.split('-').map(Number);
                return ay - by || am - bm;
            });
    }, [monthlyBuckets, currentYear, selectedKey, productionBonusRemaining, productionHistory.totalValue, productionBonusPaidThisMonth]);

    const totalRemaining = unpaidMonths.reduce((sum, m) => sum + m.remaining, 0);
    const currentRemaining = unpaidMonths.find((m) => m.key === selectedKey)?.remaining || 0;
    const overdueRemaining = Math.max(0, totalRemaining - currentRemaining);

    const totalEarnedLifetime = Object.values(monthlyBuckets).reduce((sum, b) => sum + b.earnedHours + b.earnedLeave, 0);
    const totalPaidLifetime = Object.values(monthlyBuckets).reduce((sum, b) => sum + b.paidHours + b.paidLeave, 0);
    const percentSettled = totalEarnedLifetime > 0
        ? Math.min(100, (totalPaidLifetime / totalEarnedLifetime) * 100)
        : 100;

    const roleLabel = employee.isDriver ? 'Kierowca' : 'Produkcja';

    return (
        <Box
            sx={{
                border: '1px solid',
                borderColor: 'secondary.light',
                borderLeft: '4px solid',
                borderLeftColor: 'primary.light',
                borderRadius: '12px',
                backgroundColor: 'background.paper',
                mb: 1.5,
                opacity: loading ? 0.7 : 1,
                transition: 'opacity 0.15s ease',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, px: 2.5, py: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 190 }}>
                    <Avatar sx={{ bgcolor: 'primary.light', width: 38, height: 38, fontSize: '0.85rem' }}>
                        {getInitials(employee.name, employee.surname)}
                    </Avatar>
                    <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                            {employee.name} {employee.surname}
                        </Typography>
                        <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                            {roleLabel} · {(employee.hourly_rate || 0).toFixed(0)} zł/h
                        </Typography>
                    </Box>
                </Box>

                <Divider orientation="vertical" flexItem sx={{ borderColor: 'secondary.light' }} />

                <MetricBlock
                    label="Godziny pracy"
                    value={fmtHM(monthHoursMinutes)}
                    valueColor={HOURS_COLOR}
                    lines={[
                        fmtPln(monthSalary),
                        monthPaidAmount > 0.005 ? `zapł. ${fmtPln(monthPaidAmount)}` : 'nic nie zapłacono',
                    ]}
                />

                <MetricBlock
                    label="Nadg. / dodatki"
                    value={fmtPln(productionHistory.totalValue)}
                    valueColor={productionHistory.totalValue > 0.005 ? HOURS_COLOR : 'text.disabled'}
                    lines={[
                        productionBonusPaidThisMonth > 0.005 ? `zapł. ${fmtPln(productionBonusPaidThisMonth)}` : 'nic nie zapłacono',
                    ]}
                />

                <MetricBlock
                    label="Urlop"
                    value={`${monthLeaveHours}h 00m`}
                    lines={monthLeaveHours > 0 ? [
                        fmtPln(monthLeaveAmount),
                        monthLeavePaid > 0.005 ? `zapł. ${fmtPln(monthLeavePaid)}` : 'nic nie zapłacono',
                    ] : ['—']}
                />

                <Box sx={{ flex: 1, minWidth: 220 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                        <Typography
                            sx={{
                                fontSize: '0.65rem',
                                fontWeight: 700,
                                letterSpacing: '0.5px',
                                color: 'text.secondary',
                                textTransform: 'uppercase',
                            }}
                        >
                            Nierozliczone okresy
                        </Typography>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary' }}>
                            {Math.round(percentSettled)}% rozliczone
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {unpaidMonths.length === 0 ? (
                            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                brak zaległości
                            </Typography>
                        ) : unpaidMonths.map((m) => (
                            <Box
                                key={m.key}
                                onClick={() => { setModalPeriodKey(m.key); setModalOpen(true); }}
                                sx={{
                                    px: 1.25,
                                    py: 0.5,
                                    borderRadius: '8px',
                                    backgroundColor: m.key === selectedKey ? 'secondary.light' : '#FBE3D2',
                                    display: 'flex',
                                    gap: 1,
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    '&:hover': { opacity: 0.85 },
                                }}
                            >
                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>
                                    {monthLabel(m.key)}
                                </Typography>
                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'primary.main' }}>
                                    {fmtPln(m.remaining)}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    <LinearProgress
                        variant="determinate"
                        value={percentSettled}
                        sx={{
                            mt: 1,
                            height: 4,
                            borderRadius: 2,
                            backgroundColor: 'secondary.light',
                            '& .MuiLinearProgress-bar': { backgroundColor: 'primary.main' },
                        }}
                    />
                </Box>

                <Box sx={{ textAlign: 'right', minWidth: 140 }}>
                    <Typography
                        sx={{
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            letterSpacing: '0.5px',
                            color: 'text.secondary',
                            textTransform: 'uppercase',
                        }}
                    >
                        Do zapłaty
                    </Typography>
                    <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, color: 'primary.main' }}>
                        {fmtPln(currentRemaining)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: overdueRemaining > 0.005 ? REMAINING_COLOR : 'text.secondary' }}>
                        {overdueRemaining > 0.005 ? `+ ${fmtPln(overdueRemaining)} zaległe` : 'brak zaległości'}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={() => { setModalPeriodKey(unpaidMonths[0]?.key || null); setModalOpen(true); }}
                    >
                        Rozlicz
                    </Button>
                    <IconButton
                        onClick={() => setOpen((o) => !o)}
                        sx={{ border: '1px solid', borderColor: 'secondary.light', borderRadius: '8px' }}
                    >
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </Box>
            </Box>

            <Collapse in={open} timeout="auto" unmountOnExit>
                <Box
                    sx={{
                        borderTop: '1px solid',
                        borderColor: 'secondary.light',
                        px: 2.5,
                        py: 2,
                        backgroundColor: 'rgba(0,0,0,0.015)',
                        overflowX: 'hidden',
                    }}
                >
                    <Box sx={{ display: 'flex', gap: 5, flexWrap: 'wrap', maxWidth: '100%' }}>
                        <Box sx={{ flex: '1 1 320px', minWidth: 0, maxWidth: '100%' }}>
                            <Typography sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.75rem', mb: 1 }}>
                                Historia obecności
                            </Typography>
                            {monthAttendanceList.length === 0 ? (
                                <Typography variant="body2" sx={{ color: 'text.secondary', pb: 1 }}>
                                    Brak obecności w tym okresie
                                </Typography>
                            ) : (
                                <Box sx={{ overflowX: 'auto' }}>
                                    <Table size="small" sx={{ width: 'auto', minWidth: 360 }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem', py: 0.5, pl: 0 }}>Data</TableCell>
                                                <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem', py: 0.5 }}>Wejście</TableCell>
                                                <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem', py: 0.5 }}>Wyjście</TableCell>
                                                <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem', py: 0.5 }} align="right">Czas</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {[...monthAttendanceList]
                                                .sort((a, b) => new Date(b.check_in) - new Date(a.check_in))
                                                .map((r, i) => (
                                                    <TableRow key={i}>
                                                        <TableCell sx={{ py: 0.5, pl: 0, fontSize: '0.85rem', color: 'text.secondary', border: 0 }}>
                                                            {fmtDate(r.check_in)}
                                                        </TableCell>
                                                        <TableCell sx={{ py: 0.5, fontSize: '0.85rem', color: 'text.secondary', border: 0 }}>
                                                            {fmtTime(r.check_in)}
                                                        </TableCell>
                                                        <TableCell sx={{ py: 0.5, fontSize: '0.85rem', color: r.check_out ? 'text.secondary' : 'warning.main', border: 0 }}>
                                                            {r.check_out ? fmtTime(r.check_out) : 'pracuje'}
                                                        </TableCell>
                                                        <TableCell sx={{ py: 0.5, fontSize: '0.85rem', color: 'text.secondary', border: 0 }} align="right">
                                                            {fmtHM(billableMinutesOf(r, rules))}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                        </TableBody>
                                    </Table>
                                </Box>
                            )}
                        </Box>

                        <Box sx={{ flex: '1 1 320px', minWidth: 0, maxWidth: '100%' }}>
                            <Typography sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.75rem', mb: 1 }}>
                                Historia wypłat
                            </Typography>
                            <PaymentHistory
                                payments={monthPayments}
                                onDeleted={handlePaymentChanged}
                                onEdited={handlePaymentChanged}
                            />
                        </Box>

                        <Box sx={{ flex: '1 1 100%', minWidth: 0, maxWidth: '100%' }}>
                            <Typography sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.75rem', mb: 1 }}>
                                Historia produkcji
                            </Typography>
                            {productionHistory.rows.length === 0 ? (
                                <Typography variant="body2" sx={{ color: 'text.secondary', pb: 1 }}>
                                    Brak sesji na maszynach w tym okresie
                                </Typography>
                            ) : (
                                <Box sx={{ overflowX: 'auto' }}>
                                <Table size="small" sx={{ width: 'auto', minWidth: 460 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem', py: 0.5, pl: 0 }}>Tydzień</TableCell>
                                            <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem', py: 0.5 }}>Stanowisko</TableCell>
                                            <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem', py: 0.5 }}>Stawka</TableCell>
                                            <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem', py: 0.5 }} align="right">Przerób</TableCell>
                                            <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem', py: 0.5 }} align="right">Norma</TableCell>
                                            <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem', py: 0.5 }} align="right">Ponad normę</TableCell>
                                            <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem', py: 0.5 }} align="right">Dodatek</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {productionHistory.rows.map((r) => (
                                            <TableRow key={`${r.employeeId}|${r.deviceId}|${r.weekKey}`}>
                                                <TableCell sx={{ py: 0.5, pl: 0, fontSize: '0.85rem', color: 'text.secondary', border: 0 }}>
                                                    {fmtWeekRange(r.weekStart, r.weekEnd)}
                                                </TableCell>
                                                <TableCell sx={{ py: 0.5, fontSize: '0.85rem', color: 'text.secondary', border: 0 }}>
                                                    {r.standName}
                                                </TableCell>
                                                <TableCell sx={{ py: 0.5, fontSize: '0.85rem', color: 'text.secondary', border: 0 }}>
                                                    {r.bonusRate != null ? `${r.bonusRate.toFixed(2)} zł/jedn.` : '—'}
                                                </TableCell>
                                                <TableCell sx={{ py: 0.5, fontSize: '0.85rem', color: 'text.secondary', border: 0 }} align="right">
                                                    {r.quantity != null ? `${r.quantity.toFixed(1)} ${r.unit}` : '—'}
                                                </TableCell>
                                                <TableCell sx={{ py: 0.5, fontSize: '0.85rem', color: 'text.secondary', border: 0 }} align="right">
                                                    {r.weeklyTarget != null ? `${r.weeklyTarget.toFixed(1)} ${r.unit}` : '—'}
                                                </TableCell>
                                                <TableCell sx={{ py: 0.5, fontSize: '0.85rem', color: 'text.secondary', border: 0 }} align="right">
                                                    {r.unitsOverNorma != null ? `${r.unitsOverNorma.toFixed(1)} ${r.unit}` : '—'}
                                                </TableCell>
                                                <TableCell sx={{ py: 0.5, fontSize: '0.85rem', color: 'text.secondary', border: 0 }} align="right">
                                                    {fmtPln(r.value)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow>
                                            <TableCell colSpan={6} sx={{ py: 0.5, pl: 0, fontSize: '0.85rem', fontWeight: 700, border: 0 }}>
                                                Suma
                                            </TableCell>
                                            <TableCell sx={{ py: 0.5, fontSize: '0.85rem', fontWeight: 700, border: 0 }} align="right">
                                                {fmtPln(productionHistory.totalValue)}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Collapse>

            <PaymentsModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                employee={employee}
                idConfig={idConfig}
                unpaidPeriods={unpaidMonths}
                defaultPeriodKey={modalPeriodKey}
                onSaved={handlePaymentChanged}
            />
        </Box>
    );
}

export default PaymentCardComponent;
